import React, { useCallback, useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { GraduationCapIcon, MapPinIcon, PhoneIcon, MailIcon, CheckCircleIcon, BookOpenIcon, BriefcaseIcon, ChevronDownIcon, ChevronLeftIcon, AwardIcon, BuildingIcon, LoaderIcon } from 'lucide-react';
import { getUniversityBySlug, getProgramBySlug, University, UniversityProgram } from '../data/universities';
import { useFormState } from '../hooks/useFormState';
import { formatPhone, stripPhone } from '../utils/phoneFormat';
import { educationLevels } from '../data/degrees';
import { TrustSignals } from '../components/TrustSignals';
import { usePageMeta } from '../hooks/usePageMeta';
const startDateOptions = ['Immediately', 'Within 1 month', 'Within 3 months', 'Within 6 months', 'Within 1 year', 'Not sure yet'];
function getGradYearOptions(): string[] {
  const currentYear = new Date().getFullYear();
  const years: string[] = [];
  for (let y = currentYear + 2; y >= currentYear - 30; y--) {
    years.push(String(y));
  }
  return years;
}
const gradYearOptions = getGradYearOptions();
export function UniversityPage() {
  const {
    universitySlug,
    programSlug
  } = useParams<{
    universitySlug: string;
    programSlug?: string;
  }>();
  const navigate = useNavigate();
  const [university, setUniversity] = useState<University | null>(null);
  usePageMeta(university ? `${university.name} Online Programs | OnlineCollegeGuide` : 'College Programs | OnlineCollegeGuide', university ? `Explore online degree programs at ${university.name}. ${university.description?.slice(0, 120) || 'Compare programs and request information.'}` : 'Explore online degree programs at accredited colleges and universities.', {
    ogImage: university?.logoUrl || 'https://images.unsplash.com/photo-1562774053-701939374585?w=1200&h=630&fit=crop&q=80',
    ogType: 'website'
  });
  const [selectedProgram, setSelectedProgram] = useState<UniversityProgram | null>(null);
  const [formStep, setFormStep] = useState(0); // 0=program select, 1=education, 2=zip/address, 3=contact
  const [zipLoading, setZipLoading] = useState(false);
  const [zipLookedUp, setZipLookedUp] = useState(false);
  const {
    formData,
    errors,
    updateFormData,
    updateMultiple,
    validateUniStep1,
    validateUniStep2,
    validateUniStep3
  } = useFormState();
  useEffect(() => {
    if (universitySlug) {
      const foundUni = getUniversityBySlug(universitySlug);
      if (foundUni) {
        setUniversity(foundUni);
        if (programSlug) {
          const foundProg = getProgramBySlug(foundUni, programSlug);
          if (foundProg) {
            setSelectedProgram(foundProg);
            if (formStep === 0) setFormStep(1);
          } else {
            setSelectedProgram(null);
          }
        } else {
          setSelectedProgram(null);
          setFormStep(0);
        }
      } else {
        setUniversity(null);
      }
    }
    window.scrollTo(0, 0);
  }, [universitySlug, programSlug]);
  const handleProgramChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newSlug = e.target.value;
    if (newSlug) {
      navigate(`/colleges/${universitySlug}/${newSlug}`, {
        replace: true
      });
    } else {
      navigate(`/colleges/${universitySlug}`, {
        replace: true
      });
    }
  };
  const lookupZip = useCallback(async (zip: string) => {
    if (zip.length < 5) return;
    setZipLoading(true);
    try {
      const res = await fetch(`https://api.zippopotam.us/us/${zip}`);
      if (res.ok) {
        const data = await res.json();
        const place = data.places?.[0];
        if (place) {
          updateMultiple({
            city: place['place name'] || '',
            state: place['state abbreviation'] || ''
          });
          setZipLookedUp(true);
        }
      } else {
        // Fallback: let user enter manually
        setZipLookedUp(true);
      }
    } catch {
      setZipLookedUp(true);
    } finally {
      setZipLoading(false);
    }
  }, [updateMultiple]);
  const handleStep1Next = () => {
    if (validateUniStep1()) setFormStep(2);
  };
  const handleStep2Next = () => {
    if (validateUniStep2()) {
      lookupZip(formData.zipCode);
      setFormStep(3);
    }
  };
  const handleStep3Submit = () => {
    if (validateUniStep3()) {
      const cleanedFormData = {
        ...formData,
        phone: stripPhone(formData.phone)
      };
      console.log('Lead submitted:', cleanedFormData);
      navigate('/thank-you', {
        state: {
          formData: cleanedFormData,
          partner: university ? {
            name: university.name,
            logo: university.logoUrl || '',
            primaryColor: '#14479E',
            accentColor: '#5FA0EE',
            backUrl: `/colleges/${university.slug}`,
            programTitle: selectedProgram?.title || ''
          } : undefined
        }
      });
    }
  };
  const totalSteps = 3;
  const progressPercent = formStep === 0 ? 0 : Math.round(formStep / totalSteps * 100);
  const selectClasses = (hasError: boolean) => `block w-full px-3 py-3 text-sm border rounded-md bg-white appearance-none cursor-pointer ${hasError ? 'border-red-400' : 'border-gray-300 focus:ring-navy focus:border-navy'}`;
  const inputClasses = (hasError: boolean) => `block w-full px-3 py-3 text-sm border rounded-md shadow-sm ${hasError ? 'border-red-400' : 'border-gray-300 focus:ring-navy focus:border-navy'}`;
  if (!university) {
    return <div className="min-h-[60vh] flex flex-col items-center justify-center bg-light px-4" data-id="element-3512" data-mp="3ejjp7hj60g5z">
        <h1 className="text-3xl font-bold text-navy mb-4" data-id="element-3513" data-mp="39mkuoxvla779">
          University Not Found
        </h1>
        <p className="text-gray-600 mb-8" data-id="element-3514" data-mp="34ojdyjlmyjyy">
          We couldn't find the university you're looking for.
        </p>
        <Link to="/" className="text-white bg-navy px-6 py-2 rounded-md hover:bg-navy-light transition-colors" data-id="element-3515" data-mp="30nfw5rk12dv3">
          Return Home
        </Link>
      </div>;
  }
  const renderFormContent = () => {
    // Step 0: Just program selector (shown when no program selected)
    if (formStep === 0) {
      return <div className="space-y-4" data-id="element-3516" data-mp="2py569ihdcah8">
          <div data-id="element-3517" data-mp="2c102jp54nnp5">
            <label htmlFor="hero-program-select" className="block text-xs font-bold text-gray-700 mb-1 uppercase tracking-wider" data-id="element-3518" data-mp="2c1ia3hx8pocd">
              Select a Program
            </label>
            <div className="relative" data-id="element-3519" data-mp="34om832xx12en">
              <select id="hero-program-select" value={programSlug || ''} onChange={handleProgramChange} className="block w-full px-3 py-3 text-sm border border-gray-300 rounded-md bg-gray-50 appearance-none cursor-pointer focus:ring-navy focus:border-navy font-medium text-gray-800" data-id="element-3520" data-mp="34pnto1ck57am">
                <option value="" data-id="element-3521" data-mp="2zx4gh5hs8e6c">-- Choose a Program --</option>
                {(() => {
                const groups: Record<string, typeof university.programs> = {};
                university.programs.forEach(p => {
                  const area = p.focusArea === '4' ? 'Computer Technology' : p.focusArea === '1' ? 'Business' : 'Other';
                  if (!groups[area]) groups[area] = [];
                  groups[area].push(p);
                });
                return Object.entries(groups).map(([area, progs]) => <optgroup key={area} label={area} data-id="element-3522" data-mp="1prjlmb85jqe6">
                      {progs.map(p => <option key={p.slug} value={p.slug} data-id="element-3523" data-mp="1lq7svwtzep1r">
                          {p.title}
                        </option>)}
                    </optgroup>);
              })()}
              </select>
              <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-3 text-gray-500" data-id="element-3524" data-mp="t2kdqxbhcqmi">
                <ChevronDownIcon className="h-4 w-4" data-id="element-3525" data-mp="172cxg8ola6kz" />
              </div>
            </div>
          </div>
          <p className="text-xs text-gray-500 text-center" data-id="element-3526" data-mp="120xdkjy5e646">
            Select a program above to get started
          </p>
        </div>;
    }
    // Step 1: Education level, start date, grad year
    if (formStep === 1) {
      return <div className="space-y-4" data-id="element-3527" data-mp="2dk02wjk2fecf">
          {/* Program selector */}
          <div data-id="element-3528" data-mp="2xlbhnwxqcxv1">
            <label htmlFor="step1-program" className="block text-xs font-bold text-gray-700 mb-1 uppercase tracking-wider" data-id="element-3529" data-mp="2xhnlsr4tbzd5">
              Program
            </label>
            <div className="relative" data-id="element-3530" data-mp="36jn2tjftv3g3">
              <select id="step1-program" value={programSlug || ''} onChange={handleProgramChange} className="block w-full px-3 py-2.5 text-sm border border-gray-200 rounded-md bg-gray-50 appearance-none cursor-pointer font-medium text-gray-700" data-id="element-3531" data-mp="36ilqlbn2saws">
                {(() => {
                const groups: Record<string, typeof university.programs> = {};
                university.programs.forEach(p => {
                  const area = p.focusArea === '4' ? 'Computer Technology' : p.focusArea === '1' ? 'Business' : 'Other';
                  if (!groups[area]) groups[area] = [];
                  groups[area].push(p);
                });
                return Object.entries(groups).map(([area, progs]) => <optgroup key={area} label={area} data-id="element-3532" data-mp="1zy2orgvegmj0">
                      {progs.map(p => <option key={p.slug} value={p.slug} data-id="element-3533" data-mp="1sd8s8j2ws764">
                          {p.title}
                        </option>)}
                    </optgroup>);
              })()}
              </select>
              <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-3 text-gray-400" data-id="element-3534" data-mp="1ingvbi7c71yh">
                <ChevronDownIcon className="h-4 w-4" data-id="element-3535" data-mp="1emx0ila8kafi" />
              </div>
            </div>
          </div>

          <div className="border-t border-gray-100 pt-3" data-id="element-3536" data-mp="2b98oi62x5wvi" />

          {/* Education Level */}
          <div data-id="element-3537" data-mp="26a5jumqupso2">
            <label htmlFor="currentEducation" className="block text-xs font-bold text-gray-700 mb-1 uppercase tracking-wider" data-id="element-3538" data-mp="26af8a5w8zrrn">
              Highest Level of Education
            </label>
            <select id="currentEducation" value={formData.currentEducation} onChange={e => updateFormData('currentEducation', e.target.value)} className={selectClasses(!!errors.currentEducation)} data-id="element-3539" data-mp="1xdo6dcv0lip5">
              <option value="" data-id="element-3540" data-mp="lyhpe9u86c9g">-- Please Select --</option>
              {educationLevels.map(level => <option key={level} value={level} data-id="element-3541" data-mp="3e5gayt6g5sbq">
                  {level}
                </option>)}
            </select>
            {errors.currentEducation && <p className="mt-1 text-xs text-red-600" data-id="element-3542" data-mp="3my1uibhwk31m">
                {errors.currentEducation}
              </p>}
          </div>

          {/* Start Date */}
          <div data-id="element-3543" data-mp="3i4p7u6wsa6bi">
            <label htmlFor="startDate" className="block text-xs font-bold text-gray-700 mb-1 uppercase tracking-wider" data-id="element-3544" data-mp="qbm9tptejop">
              When Would You Like to Start?
            </label>
            <select id="startDate" value={formData.startDate} onChange={e => updateFormData('startDate', e.target.value)} className={selectClasses(!!errors.startDate)} data-id="element-3545" data-mp="3ryz8sczskcxu">
              <option value="" data-id="element-3546" data-mp="5pwn69ojp242">-- Please Select --</option>
              {startDateOptions.map(opt => <option key={opt} value={opt} data-id="element-3547" data-mp="2bj4pvt98j595">
                  {opt}
                </option>)}
            </select>
            {errors.startDate && <p className="mt-1 text-xs text-red-600" data-id="element-3548" data-mp="27iuhyb1vu4rn">{errors.startDate}</p>}
          </div>

          {/* Grad Year */}
          <div data-id="element-3549" data-mp="21e6i2fg4lw38">
            <label htmlFor="gradYear" className="block text-xs font-bold text-gray-700 mb-1 uppercase tracking-wider" data-id="element-3550" data-mp="21ey5h2zkknif">
              High School Graduation Year
            </label>
            <select id="gradYear" value={formData.gradYear} onChange={e => updateFormData('gradYear', e.target.value)} className={selectClasses(!!errors.gradYear)} data-id="element-3551" data-mp="1slusswwr0eou">
              <option value="" data-id="element-3552" data-mp="1dniw4q9dl5xo">-- Please Select --</option>
              {gradYearOptions.map(yr => <option key={yr} value={yr} data-id="element-3553" data-mp="1iphz3z17e0u3">
                  {yr}
                </option>)}
            </select>
            {errors.gradYear && <p className="mt-1 text-xs text-red-600" data-id="element-3554" data-mp="14qi8wmy1ldvt">{errors.gradYear}</p>}
          </div>

          <div className="flex gap-3" data-id="element-3555" data-mp="19p3deykbujq2">
            <button type="button" onClick={() => navigate(`/colleges/${universitySlug}`, {
            replace: true
          })} className="flex items-center justify-center px-4 py-3 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 transition-colors" data-id="element-3556" data-mp="2z1x2jeu6bxgu">
              <ChevronLeftIcon className="w-4 h-4 mr-1" data-id="element-3557" data-mp="2xkroo365o6zr" />
              Back
            </button>
            <button type="button" onClick={handleStep1Next} className="flex-1 bg-navy hover:bg-navy-light text-white font-bold py-3.5 px-6 rounded-md text-sm tracking-wider uppercase transition-colors duration-200 shadow-md" data-id="element-3558" data-mp="2sl4zqwgyt2b4">
              Next
            </button>
          </div>
        </div>;
    }
    // Step 2: Zip code → city/state/address
    if (formStep === 2) {
      return <div className="space-y-4" data-id="element-3559" data-mp="3bksmtav1d0bj">
          <div data-id="element-3560" data-mp="3bjr3yk7s39j9">
            <label htmlFor="zipCode" className="block text-xs font-bold text-gray-700 mb-1 uppercase tracking-wider" data-id="element-3561" data-mp="3bj7wjxfr83jh">
              Zip Code
            </label>
            <input type="text" id="zipCode" value={formData.zipCode} onChange={e => {
            const val = e.target.value.replace(/\D/g, '').slice(0, 5);
            updateFormData('zipCode', val);
            setZipLookedUp(false);
            if (val.length === 5) {
              lookupZip(val);
            }
          }} className={inputClasses(!!errors.zipCode)} placeholder="12345" maxLength={5} data-id="element-3562" data-mp="7w7l67kt02f1" />
            {errors.zipCode && <p className="mt-1 text-xs text-red-600" data-id="element-3563" data-mp="kdicgif01udz">{errors.zipCode}</p>}
            {zipLoading && <div className="mt-2 flex items-center text-xs text-gray-500" data-id="element-3564" data-mp="pefpyhp2e095">
                <LoaderIcon className="w-3 h-3 mr-1 animate-spin" data-id="element-3565" data-mp="tfj6l6exs0bf" />
                Looking up your location...
              </div>}
          </div>

          {zipLookedUp && <>
              <div className="grid grid-cols-2 gap-3" data-id="element-3566" data-mp="134syom9s5izj">
                <div data-id="element-3567" data-mp="135b4a9ic19k4">
                  <label htmlFor="city" className="block text-xs font-bold text-gray-700 mb-1 uppercase tracking-wider" data-id="element-3568" data-mp="136vxnvswumge">
                    City
                  </label>
                  <input type="text" id="city" value={formData.city} onChange={e => updateFormData('city', e.target.value)} className="block w-full px-3 py-3 text-sm border border-gray-300 rounded-md shadow-sm focus:ring-navy focus:border-navy bg-gray-50" placeholder="City" data-id="element-3569" data-mp="1d4a0gslwvcq8" />
                </div>
                <div data-id="element-3570" data-mp="29tknlx4fdqdj">
                  <label htmlFor="state" className="block text-xs font-bold text-gray-700 mb-1 uppercase tracking-wider" data-id="element-3571" data-mp="29s9jfw72iglw">
                    State
                  </label>
                  <input type="text" id="state" value={formData.state} onChange={e => updateFormData('state', e.target.value)} className="block w-full px-3 py-3 text-sm border border-gray-300 rounded-md shadow-sm focus:ring-navy focus:border-navy bg-gray-50" placeholder="State" data-id="element-3572" data-mp="2jlgjr2myb6f4" />
                </div>
              </div>

              <div data-id="element-3573" data-mp="24wkd9a0jm1gm">
                <label htmlFor="address" className="block text-xs font-bold text-gray-700 mb-1 uppercase tracking-wider" data-id="element-3574" data-mp="1zw66dvaogxk6">
                  Street Address{' '}
                  <span className="text-gray-400 font-normal normal-case" data-id="element-3575" data-mp="3g8uzexks6zc3">
                    (optional)
                  </span>
                </label>
                <input type="text" id="address" value={formData.address} onChange={e => updateFormData('address', e.target.value)} className="block w-full px-3 py-3 text-sm border border-gray-300 rounded-md shadow-sm focus:ring-navy focus:border-navy" placeholder="123 Main St" data-id="element-3576" data-mp="3jslo9w5dooyu" />
              </div>
            </>}

          <div className="flex gap-3" data-id="element-3577" data-mp="3esh7e50k5s83">
            <button type="button" onClick={() => setFormStep(1)} className="flex items-center justify-center px-4 py-3 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 transition-colors" data-id="element-3578" data-mp="3etiw3cahyd8a">
              <ChevronLeftIcon className="w-4 h-4 mr-1" data-id="element-3579" data-mp="3st1q716h3o1l" />
              Back
            </button>
            <button type="button" onClick={handleStep2Next} className="flex-1 bg-navy hover:bg-navy-light text-white font-bold py-3.5 px-6 rounded-md text-sm tracking-wider uppercase transition-colors duration-200 shadow-md" data-id="element-3580" data-mp="1mqu7jgbldh4">
              Next
            </button>
          </div>
        </div>;
    }
    // Step 3: Contact info + TCPA
    if (formStep === 3) {
      return <div className="space-y-4" data-id="element-3581" data-mp="13y8t5rqekgmv">
          <div className="grid grid-cols-2 gap-3" data-id="element-3582" data-mp="z06ye9jm6svk">
            <div data-id="element-3583" data-mp="z0gory8kn21s">
              <label htmlFor="firstName" className="block text-xs font-bold text-gray-700 mb-1 uppercase tracking-wider" data-id="element-3584" data-mp="yxui00ti8qkm">
                First Name
              </label>
              <input type="text" id="firstName" value={formData.firstName} onChange={e => updateFormData('firstName', e.target.value)} className={inputClasses(!!errors.firstName)} placeholder="Jane" data-id="element-3585" data-mp="pyh8x99am40f" />
              {errors.firstName && <p className="mt-1 text-xs text-red-600" data-id="element-3586" data-mp="g84mtclty2s6">{errors.firstName}</p>}
            </div>

            <div data-id="element-3587" data-mp="bamdvm7tbnk6">
              <label htmlFor="lastName" className="block text-xs font-bold text-gray-700 mb-1 uppercase tracking-wider" data-id="element-3588" data-mp="b805jkd3l40w">
                Last Name
              </label>
              <input type="text" id="lastName" value={formData.lastName} onChange={e => updateFormData('lastName', e.target.value)} className={inputClasses(!!errors.lastName)} placeholder="Doe" data-id="element-3589" data-mp="29540ll00i3x" />
              {errors.lastName && <p className="mt-1 text-xs text-red-600" data-id="element-3590" data-mp="2901d7uluvj91">{errors.lastName}</p>}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3" data-id="element-3591" data-mp="2dyusnskpdkbu">
            <div data-id="element-3592" data-mp="2hzog6ku8gx47">
              <label htmlFor="email" className="block text-xs font-bold text-gray-700 mb-1 uppercase tracking-wider" data-id="element-3593" data-mp="2hz4viwd77umh">
                Email Address
              </label>
              <input type="email" id="email" value={formData.email} onChange={e => updateFormData('email', e.target.value)} className={inputClasses(!!errors.email)} placeholder="jane@example.com" data-id="element-3594" data-mp="1q88ls4mfq63f" />
              {errors.email && <p className="mt-1 text-xs text-red-600" data-id="element-3595" data-mp="1z7c436ds2hgv">{errors.email}</p>}
            </div>

            <div data-id="element-3596" data-mp="23zvpgpipigw8">
              <label htmlFor="phone" className="block text-xs font-bold text-gray-700 mb-1 uppercase tracking-wider" data-id="element-3597" data-mp="240exneik1qx2">
                Phone Number
              </label>
              <input type="tel" id="phone" value={formData.phone} onChange={e => updateFormData('phone', formatPhone(e.target.value))} className={inputClasses(!!errors.phone)} placeholder="(555)123-4567" maxLength={13} data-id="element-3598" data-mp="1bhri0embv43d" />
              {errors.phone && <p className="mt-1 text-xs text-red-600" data-id="element-3599" data-mp="3w3cq4h54nzqx">{errors.phone}</p>}
            </div>
          </div>

          <div className="space-y-2" data-id="element-3600" data-mp="dtsq7btj1411">
            <label className="flex items-start gap-2.5 cursor-pointer" data-id="element-3601" data-mp="9ts9tlmvairc">
              <input type="checkbox" checked={formData.tcpaConsent} onChange={e => updateFormData('tcpaConsent', e.target.checked)} className="mt-0.5 h-4 w-4 rounded border-gray-300 text-navy focus:ring-navy cursor-pointer flex-shrink-0" data-id="element-3602" data-mp="9wo6l6mzb1e7" />
              <span className="text-xs text-gray-600 leading-relaxed" data-id="element-3603" data-mp="xk5x6yuibtfl">
                By submitting this form, you are giving your express written
                consent to be contacted regarding our services using email,
                telephone or text, including our use of automated technology for
                calls or texts to the number provided above. This consent is not
                required to purchase goods or services. and you may always call
                us directly.
              </span>
            </label>
            {errors.tcpaConsent && <p className="text-xs text-red-600" data-id="element-3604" data-mp="1q402r0oh35h8">{errors.tcpaConsent}</p>}
          </div>

          <div className="flex gap-3" data-id="element-3605" data-mp="1c8o2yevgmotg">
            <button type="button" onClick={() => setFormStep(2)} className="flex items-center justify-center px-4 py-3 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 transition-colors" data-id="element-3606" data-mp="1g7na864nyxo2">
              <ChevronLeftIcon className="w-4 h-4 mr-1" data-id="element-3607" data-mp="1792yxq1fnq9v" />
              Back
            </button>
            <button type="button" onClick={handleStep3Submit} className="flex-1 bg-cta hover:bg-cta-dark text-white font-bold py-3.5 px-6 rounded-md text-sm tracking-wider uppercase transition-colors duration-200 shadow-md" data-id="element-3608" data-mp="sk6ue2yl4ve0">
              Submit
            </button>
          </div>
        </div>;
    }
    return null;
  };
  return <main className="flex-grow bg-light pb-16" data-id="element-3609" data-mp="oj3itsnkllil">
      {/* College Header */}
      <header className="bg-white border-b border-gray-200" data-id="element-3610" data-mp="mmkfvongjrxx">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3 flex items-center justify-between" data-id="element-3611" data-mp="rngscqlvkmey">
          <Link to={`/colleges/${universitySlug}`} className="flex items-center" data-id="element-3612" data-mp="do7ehxl564e5">
            <img src="https://cdn.magicpatterns.com/uploads/kMxFqqE7MbeZ2oDSzfEEA9/GTC_LOGO_(1).webp" alt={`${university.name} logo`} className="h-14 md:h-16 w-auto object-contain" data-id="element-3613" data-mp="im0gsfjbqe95" />
          </Link>
          <div className="flex items-center gap-4 text-sm text-gray-600" data-id="element-3614" data-mp="1ae7xffytxqnj">
            <span className="hidden sm:inline-flex items-center" data-id="element-3615" data-mp="1afj01cgjgsd2">
              <PhoneIcon className="w-4 h-4 mr-1.5 text-gray-400" data-id="element-3616" data-mp="wls0mwwiuov2" />
              {university.phone}
            </span>
            <a href={`mailto:${university.email}`} className="hidden md:inline-flex items-center hover:text-navy transition-colors" data-id="element-3617" data-mp="11j9ztvkqlppv">
              <MailIcon className="w-4 h-4 mr-1.5 text-gray-400" data-id="element-3618" data-mp="1u1fg3pz09i7f" />
              {university.email}
            </a>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <div className="hero-bg relative overflow-hidden" data-id="element-3619" data-mp="1zevtyzoidr1v">
        <div aria-hidden="true" className="absolute inset-0 pointer-events-none bg-no-repeat bg-cover bg-center mix-blend-soft-light opacity-70" style={{
        backgroundImage: "url('https://cdn.magicpatterns.com/uploads/8uFwSQxxc9xYA37moQruEX/ChatGPT_Image_May_6,_2026,_08_20_39_PM.png')"
      }} data-id="element-3620" data-mp="1zee0ubx57sy6" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 py-10 md:py-16" data-id="element-3621" data-mp="1ki52dcl2tjmt">
          <div className="inline-flex items-center text-gold font-semibold tracking-wider text-sm uppercase mb-6" data-id="element-3622" data-mp="1ghmc3myu0lmq">
            <BuildingIcon className="w-4 h-4 mr-2" data-id="element-3623" data-mp="1udq0gzn0xzra" />
            {university.name} &middot; {university.location}
          </div>

          <div className="flex flex-col lg:flex-row gap-8 lg:gap-12 items-start" data-id="element-3624" data-mp="35upqtppta4io">
            {/* Left: Program Info */}
            <div className="lg:w-1/2 flex flex-col justify-center" data-id="element-3625" data-mp="30wo7d3oh436o">
              <AnimatePresence mode="wait" data-id="element-3626" data-mp="38wkskyg2r9uz">
                <motion.div key={selectedProgram?.slug || 'overview'} initial={{
                opacity: 0,
                y: 10
              }} animate={{
                opacity: 1,
                y: 0
              }} exit={{
                opacity: 0,
                y: -10
              }} transition={{
                duration: 0.25
              }} data-id="element-3627" data-mp="38vswlmikfa46">
                  <h1 className="text-3xl md:text-4xl lg:text-[2.75rem] font-extrabold text-white mb-5 leading-tight" data-id="element-3628" data-mp="2p8v9rgq48jxw">
                    {selectedProgram ? selectedProgram.title : `Explore Programs at ${university.name}`}
                  </h1>
                  <p className="text-base md:text-lg text-gray-200 leading-relaxed mb-6" data-id="element-3629" data-mp="2z56pkgowzrx3">
                    {selectedProgram ? selectedProgram.description : university.description}
                  </p>
                </motion.div>
              </AnimatePresence>

              {!selectedProgram && <div className="inline-flex items-center px-4 py-2 rounded-full bg-white/10 border border-white/20 text-white text-sm font-medium self-start" data-id="element-3630" data-mp="2bfvu9eqc2pqc">
                  <ChevronDownIcon className="w-4 h-4 mr-2 animate-bounce" data-id="element-3631" data-mp="1yyuifxjk3miu" />
                  Select a program to get started
                </div>}

              {selectedProgram && <div className="flex flex-wrap gap-3 text-sm text-gray-300" data-id="element-3632" data-mp="1u1kxau379x0t">
                  {selectedProgram.keyFacts.slice(0, 2).map((fact, idx) => <span key={idx} className="inline-flex items-center bg-white/10 border border-white/15 rounded-full px-3 py-1" data-id="element-3633" data-mp="1kyjt4yxwafzs" data-mpi={idx}>
                      <CheckCircleIcon className="w-3.5 h-3.5 text-green-400 mr-1.5" data-id="element-3634" data-mp="1g7bgzv56kgix" />
                      {fact}
                    </span>)}
                </div>}
            </div>

            {/* Right: Form Card */}
            <div className="lg:w-1/2 w-full flex justify-center lg:justify-end" data-id="element-3635" data-mp="16bqkudmsmah3">
              <div className="w-full max-w-md" data-id="element-3636" data-mp="119j3f8j7227d">
                <div className="bg-white rounded-xl shadow-2xl overflow-hidden border border-gray-100" data-id="element-3637" data-mp="x97j5wuqu3wf">
                  <div className="bg-navy px-6 py-3.5" data-id="element-3638" data-mp="1lzvgno35h8q5">
                    <h2 className="text-white font-bold text-center text-sm tracking-widest uppercase" data-id="element-3639" data-mp="1m0evux1f1j0h">
                      Request Information
                    </h2>
                  </div>

                  {/* Progress Bar */}
                  {formStep > 0 && <div className="px-6 pt-4" data-id="element-3640" data-mp="1h8ws6180d4bt">
                      <div className="flex justify-between items-center mb-1.5" data-id="element-3641" data-mp="1h7lo00anhuk6">
                        <span className="text-xs font-semibold text-navy uppercase tracking-wider" data-id="element-3642" data-mp="138m3n4u681tt">
                          Step {formStep} of {totalSteps}
                        </span>
                        <span className="text-xs text-gray-500" data-id="element-3643" data-mp="u9qxfsr4mrqi">
                          {progressPercent}%
                        </span>
                      </div>
                      <div className="h-1.5 w-full bg-gray-200 rounded-full overflow-hidden" data-id="element-3644" data-mp="2omzx6stveyvk">
                        <motion.div className="h-full bg-gold rounded-full" initial={{
                      width: 0
                    }} animate={{
                      width: `${progressPercent}%`
                    }} transition={{
                      duration: 0.4,
                      ease: 'easeOut'
                    }} data-id="element-3645" data-mp="2sobnz1ohdq5c" />
                      </div>
                    </div>}

                  <div className="p-6" data-id="element-3646" data-mp="m01n3uic98ji">
                    <AnimatePresence mode="wait" data-id="element-3647" data-mp="lz0ahllocdzo">
                      <motion.div key={formStep} initial={{
                      opacity: 0,
                      x: 30
                    }} animate={{
                      opacity: 1,
                      x: 0
                    }} exit={{
                      opacity: 0,
                      x: -30
                    }} transition={{
                      duration: 0.2
                    }} data-id="element-3648" data-mp="ly8cy58io66r">
                        {renderFormContent()}
                      </motion.div>
                    </AnimatePresence>
                  </div>
                </div>

                <p className="text-center text-gray-300 text-sm mt-3 italic" data-id="element-3649" data-mp="36jgzp5eyzmdo">
                  Used by{' '}
                  <strong className="text-white font-semibold" data-id="element-3650" data-mp="1aglz1fkqbhae">
                    2M+ students
                  </strong>{' '}
                  to choose the right school.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <TrustSignals data-id="element-3651" data-mp="1oflryzf9yicd" />

      {/* Program Content — Full Width */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12" data-id="element-3652" data-mp="1tdwwqvqpq2nq">
        <AnimatePresence mode="wait" data-id="element-3653" data-mp="1tbj4svrbx6ke">
          {selectedProgram ? <motion.div key={selectedProgram.slug} initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0,
          y: -20
        }} transition={{
          duration: 0.3
        }} className="space-y-8" data-id="element-3654" data-mp="1yboqlvax3yuk">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-3655" data-mp="2c568lbnyh3ih">
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 sm:p-8 h-full" data-id="element-3656" data-mp="2h2y9gv6kvzyt">
                  <div className="flex items-center mb-6 border-b border-gray-100 pb-4" data-id="element-3657" data-mp="5ge2iid6axcl">
                    <div className="p-2 bg-navy/5 rounded-lg mr-3" data-id="element-3658" data-mp="h1dbqytxe6u">
                      <BriefcaseIcon className="w-6 h-6 text-navy" data-id="element-3659" data-mp="iw25gd81qg7" />
                    </div>
                    <h2 className="text-xl font-bold text-navy" data-id="element-3660" data-mp="3smoi49wo2mnd">
                      Potential Job Opportunities
                    </h2>
                  </div>
                  <p className="text-sm text-gray-600 mb-4 font-medium" data-id="element-3661" data-mp="o5aw0k0qxltr">
                    {selectedProgram.jobOpportunitiesIntro}:
                  </p>
                  <ul className="space-y-3" data-id="element-3662" data-mp="fe28k4g8bra2">
                    {selectedProgram.jobOpportunities.map((job, idx) => <li key={idx} className="flex items-start text-sm text-gray-700" data-id="element-3663" data-mp="aez2cgoijf0i" data-mpi={idx}>
                        <span className="w-1.5 h-1.5 rounded-full bg-navy mt-1.5 mr-3 flex-shrink-0" data-id="element-3664" data-mp="ad4qrsz4sz93" />
                        <span data-id="element-3665" data-mp="17w67ts2gfxru">{job}</span>
                      </li>)}
                  </ul>
                </div>

                <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 sm:p-8 h-full" data-id="element-3666" data-mp="bg1tb6tfdjpl">
                  <div className="flex items-center mb-6 border-b border-gray-100 pb-4" data-id="element-3667" data-mp="g6ibf486xtia">
                    <div className="p-2 bg-navy/5 rounded-lg mr-3" data-id="element-3668" data-mp="1hvy1824jbtp">
                      <AwardIcon className="w-6 h-6 text-navy" data-id="element-3669" data-mp="1jp2qht6flz2" />
                    </div>
                    <h2 className="text-xl font-bold text-navy" data-id="element-3670" data-mp="6hibnilv6u31">Key Facts</h2>
                  </div>
                  <ul className="space-y-4" data-id="element-3671" data-mp="u7ck7nz5hwhh">
                    {selectedProgram.keyFacts.map((fact, idx) => <li key={idx} className="flex items-start text-sm text-gray-700" data-id="element-3672" data-mp="z54bqgvvvgl5" data-mpi={idx}>
                        <CheckCircleIcon className="w-5 h-5 text-green-500 mr-3 flex-shrink-0" data-id="element-3673" data-mp="l539oy3vu343" />
                        <span className="leading-tight pt-0.5" data-id="element-3674" data-mp="p65c1nt3ivaa">{fact}</span>
                      </li>)}
                  </ul>
                </div>
              </div>

              <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden" data-id="element-3675" data-mp="39xfmzuvri82y">
                <div className="bg-navy px-6 py-4 flex items-center" data-id="element-3676" data-mp="32dwmubudzy75">
                  <BookOpenIcon className="w-5 h-5 text-gold mr-3" data-id="element-3677" data-mp="32fzjvftyirld" />
                  <h2 className="text-lg font-bold text-white" data-id="element-3678" data-mp="2xejy1ljygh1x">
                    Program Curriculum
                  </h2>
                </div>
                <table className="min-w-full divide-y divide-gray-200" data-id="element-3679" data-mp="37d9erb047vgl">
                  <thead className="bg-gray-50" data-id="element-3680" data-mp="37b6gy4sq0y1b">
                    <tr data-id="element-3681" data-mp="2jmvo9ox6i4o4">
                      <th scope="col" className="px-6 py-3 text-left text-xs font-bold text-gray-500 uppercase tracking-wider" data-id="element-3682" data-mp="2jo6ppi35j0c2">
                        Course Name
                      </th>
                      <th scope="col" className="px-6 py-3 text-right text-xs font-bold text-gray-500 uppercase tracking-wider" data-id="element-3683" data-mp="2te9u0efq40pp">
                        Credits
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-100" data-id="element-3684" data-mp="20uug1vyil221">
                    {selectedProgram.curriculum.map((item, idx) => <tr key={idx} className={idx % 2 === 0 ? 'bg-white' : 'bg-gray-50/50'} data-id="element-3685" data-mp="vebqhpc7a9ae" data-mpi={idx}>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900" data-id="element-3686" data-mp="qnv9jv9685ja">
                          {item.course}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 text-right" data-id="element-3687" data-mp="lmo442ui3ifs">
                          {item.credits}
                        </td>
                      </tr>)}
                  </tbody>
                </table>
              </div>
            </motion.div> : <motion.div key="default-overview" initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} className="space-y-8" data-id="element-3688" data-mp="19ew9omm1274f">
              {/* Benefits of Online Learning */}
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 sm:p-8" data-id="element-3689" data-mp="zfnmcclo3qr1">
                <div className="flex items-center mb-6 border-b border-gray-100 pb-4" data-id="element-3690" data-mp="3p728pvadx832">
                  <div className="p-2 bg-navy/5 rounded-lg mr-3" data-id="element-3691" data-mp="3k5mqeazldfod">
                    <CheckCircleIcon className="w-6 h-6 text-navy" data-id="element-3692" data-mp="1zovl62ca5ola" />
                  </div>
                  <h2 className="text-xl font-bold text-navy" data-id="element-3693" data-mp="24n6umbynxx2z">
                    Benefits of Online Learning with Global Tech College
                  </h2>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-id="element-3694" data-mp="29kqcrmyheu07">
                  {[{
                icon: <AwardIcon className="w-6 h-6 text-crimson" data-id="element-3695" data-mp="2jcmjtnijpugk" />,
                title: 'High-Quality Education Experience'
              }, {
                icon: <BookOpenIcon className="w-6 h-6 text-crimson" data-id="element-3696" data-mp="2ncd6bf11hofa" />,
                title: 'Flexible Online Schedules'
              }, {
                icon: <CheckCircleIcon className="w-6 h-6 text-crimson" data-id="element-3697" data-mp="2xc40tgplo3fd" />,
                title: 'Accredited by ACCET'
              }, {
                icon: <BriefcaseIcon className="w-6 h-6 text-crimson" data-id="element-3698" data-mp="y01kugg6pr6g" />,
                title: 'Career Services Assistance'
              }, {
                icon: <GraduationCapIcon className="w-6 h-6 text-crimson" data-id="element-3699" data-mp="13078zmn8wvjs" />,
                title: '1-1 Student Consulting'
              }, {
                icon: <BuildingIcon className="w-6 h-6 text-crimson" data-id="element-3700" data-mp="qwldm7fpv9tz" />,
                title: 'Very Low Tuition Fees'
              }].map((item, idx) => <div key={idx} className="bg-gray-50 rounded-xl border border-gray-100 p-5 flex items-center gap-4" data-id="element-3701" data-mp="14x5sisiigli7" data-mpi={idx}>
                      <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-white shadow-sm flex-shrink-0" data-id="element-3702" data-mp="zyulepjlop3o">
                        {item.icon}
                      </div>
                      <h3 className="font-bold text-navy text-sm leading-tight" data-id="element-3703" data-mp="19suxxch7owmf">
                        {item.title}
                      </h3>
                    </div>)}
                </div>
              </div>

              {/* Highlights Grid */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4" data-id="element-3704" data-mp="1xlbhxskieyod">
                {[{
              icon: <AwardIcon className="w-6 h-6 text-crimson" data-id="element-3705" data-mp="1smgn1s99pjnw" />,
              title: 'Title IV Approved',
              desc: 'Federal financial aid available for eligible students'
            }, {
              icon: <GraduationCapIcon className="w-6 h-6 text-crimson" data-id="element-3706" data-mp="2g79a0vy2f3oq" />,
              title: 'VA Benefits',
              desc: 'Approved for veterans and dependents education benefits'
            }, {
              icon: <CheckCircleIcon className="w-6 h-6 text-crimson" data-id="element-3707" data-mp="276b05x0y41y8" />,
              title: 'ACCET Accredited',
              desc: 'Accrediting Council for Continuing Education & Training'
            }, {
              icon: <BriefcaseIcon className="w-6 h-6 text-crimson" data-id="element-3708" data-mp="1xazknchpqqpf" />,
              title: 'Financial Aid',
              desc: 'Available for students who qualify'
            }].map((item, idx) => <div key={idx} className="bg-white rounded-xl shadow-sm border border-gray-200 p-5 text-center" data-id="element-3709" data-mp="1oi64lcufznai" data-mpi={idx}>
                    <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-crimson/5 mb-3" data-id="element-3710" data-mp="19kvtw5pmilfu">
                      {item.icon}
                    </div>
                    <h3 className="font-bold text-navy text-sm mb-1" data-id="element-3711" data-mp="2hst13myko9oz">
                      {item.title}
                    </h3>
                    <p className="text-xs text-gray-500" data-id="element-3712" data-mp="2cx49c5ssnx38">{item.desc}</p>
                  </div>)}
              </div>

              {/* Available Programs */}
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 sm:p-8" data-id="element-3713" data-mp="235zs0zfnc058">
                <div className="flex items-center mb-6 border-b border-gray-100 pb-4" data-id="element-3714" data-mp="1y6dgcufguryj">
                  <div className="p-2 bg-navy/5 rounded-lg mr-3" data-id="element-3715" data-mp="1u40ckbka544e">
                    <BookOpenIcon className="w-6 h-6 text-navy" data-id="element-3716" data-mp="1u6wa3ys7tqsb" />
                  </div>
                  <h2 className="text-xl font-bold text-navy" data-id="element-3717" data-mp="1p7jf2qr6xden">
                    Available Programs
                  </h2>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-3718" data-mp="3jjq1klpjbgm9">
                  {university.programs.map(prog => <button key={prog.slug} onClick={() => navigate(`/colleges/${universitySlug}/${prog.slug}`, {
                replace: true
              })} className="flex items-start gap-3 p-4 rounded-lg border border-gray-200 hover:border-navy/30 hover:bg-navy/[0.02] transition-all text-left group" data-id="element-3719" data-mp="3ek3ku2ahzi8p">
                      <div className="p-1.5 bg-navy/5 rounded-md group-hover:bg-navy/10 transition-colors mt-0.5" data-id="element-3720" data-mp="3vuquw6moymsh">
                        <GraduationCapIcon className="w-4 h-4 text-navy" data-id="element-3721" data-mp="4mv4cza84ta5" />
                      </div>
                      <div data-id="element-3722" data-mp="336l9dhntctrl">
                        <h3 className="font-semibold text-navy text-sm group-hover:text-navy-light transition-colors" data-id="element-3723" data-mp="337ed09rh735i">
                          {prog.title}
                        </h3>
                        <p className="text-xs text-gray-500 mt-1" data-id="element-3724" data-mp="3d5aqv9bssc7b">
                          {prog.degreeLevel === '1' ? 'Certificate' : 'Associate Degree'}{' '}
                          · {prog.keyFacts[0]}
                        </p>
                      </div>
                    </button>)}
                </div>
              </div>

              {/* Accreditation */}
              <div className="bg-navy/5 rounded-xl border border-navy/10 p-6 flex items-start gap-4" data-id="element-3725" data-mp="2tdwy6u9hx11f">
                <div className="p-2 bg-white rounded-lg shadow-sm" data-id="element-3726" data-mp="2oflqoq6ob2md">
                  <AwardIcon className="w-6 h-6 text-navy" data-id="element-3727" data-mp="32g6a9okex2gx" />
                </div>
                <div data-id="element-3728" data-mp="2yekqrkddhpxr">
                  <h3 className="font-bold text-navy mb-1" data-id="element-3729" data-mp="2ye1fgmi89zsp">Accreditation</h3>
                  <p className="text-sm text-gray-600" data-id="element-3730" data-mp="2aogw42i5x0iw">
                    The Accrediting Council for Continuing Education & Training
                    (ACCET)
                  </p>
                  <p className="text-sm text-gray-500 mt-1" data-id="element-3731" data-mp="2jh0nqj01e4u8">
                    {university.location}
                  </p>
                </div>
              </div>
            </motion.div>}
        </AnimatePresence>
      </div>
    </main>;
}